import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { AlertContext } from "@actinc/dls/context";
import { GridGenerator } from "@actinc/dls";
import { OpenInNew as OpenInNewIcon } from "@actinc/dls/icons";

import _ from "lodash/fp";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import { useClient } from "urql";
import { useForm, useFormState } from "react-hook-form";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
  Check,
  FileDelimitedOutline as ChooseFileIcon,
} from "@actinc/dls/icons";

import { ControlledFormSelect, DataGrid } from "../../../components";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";
import { StandardsContext } from "../../../standardsProvider";

import { asyncPipe, setTitle } from "../../../services/utils";

import {
  checkFailedRequestData,
  hideUpdatedFields,
  parseCSV,
  saveData,
  validateCSV,
  validateData,
} from "./bdu";

const BatchDataUpload = () => {
  const { objectType } = useParams();
  const inputRef = useRef(null);
  const client = useClient();

  const [alertMessages, setAlertMessages] = useState({});
  const [file, setFile] = useState(null);
  const [validFileType, setValidFileType] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploadIsValid, setUploadIsValid] = useState(false);
  const [validatedData, setValidatedData] = useState([]);
  const [metadataListUrl, setMetadataListUrl] = useState();

  const alertContext = useContext(AlertContext);
  const allPrograms = useContext(ProgramsContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const users = useContext(UsersContext);
  const standards = useContext(StandardsContext);

  const userAccessiblePrograms = allPrograms.filter(({ _actions }) =>
    _actions.includes("batchDataUpload"),
  );

  const { control, handleSubmit, reset, watch } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: {
      program: "",
    },
  });

  useEffect(() => {
    setTitle(["CDE", "Batch Data Upload", _.capitalize(objectType)]);
  }, [objectType]);

  const selectedProgram = watch("program");
  const { isSubmitSuccessful } = useFormState({ control });

  const handleResets = useCallback(() => {
    reset({ program: "" });
    setAlertMessages(null);
    setFile(null);
    setValidatedData([]);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [reset]);

  useEffect(() => {
    handleResets();
  }, [objectType, handleResets]);

  useEffect(() => {
    if (selectedProgram) {
      const program = allPrograms.find(({ name }) => name === selectedProgram);
      setMetadataListUrl(
        `/display-metadata-list?objectType=${objectType}&programId=${program.id}`,
      );
    }
  }, [allPrograms, objectType, selectedProgram]);

  if (!userAccessiblePrograms?.length) return null;

  const fileInputOnChange = (e) => {
    // Reset the UI
    setAlertMessages(null);
    setValidatedData([]);

    const file = e.target.files[0];
    setValidFileType(
      ["csv"].includes(file.name.split(".").at(-1).toLowerCase()),
    );
    setFile(e.target.files[0]);

    // Clear the file selector
    reset({ program: selectedProgram });
  };

  const fileInputOnClick = (e) => {
    inputRef.current.value = "";
    inputRef.current.click();
    setAlertMessages(null);
    setFile(null);
    setValidatedData([]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setValidatedData([]);
    if (!file) return;

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      const csvFile = event.target.result;
      const parsedCSV = await parseCSV(csvFile);

      // Validate the CSV structure
      const { invalidFields, message } = validateCSV({
        metadataFields: metadataConfiguration[objectType],
        type: objectType,
      })(parsedCSV);

      if (message) {
        return setAlertMessages({
          message,
          severity: "error",
          title: `Invalid File`,
        });
      }

      if (invalidFields?.length) {
        return setAlertMessages({
          message: (
            <ul>
              {invalidFields.map((header) => (
                <li key={header}>{header}</li>
              ))}
            </ul>
          ),
          severity: "error",
          title: `Invalid header${invalidFields.length > 1 ? "s" : ""}`,
        });
      }

      setLoading(true);

      // Validate the CSV data
      const program = allPrograms.find(({ name }) => name === selectedProgram);
      const validatedCSV = await validateData({
        client,
        metadataFields: metadataConfiguration[objectType],
        program,
        standards,
        type: objectType,
        users,
      })(parsedCSV.data);
      setValidatedData(validatedCSV);
      setUploadIsValid(
        !alertMessages &&
          validatedCSV.every(
            (o) => !o.invalidFields.length && !o.missingRequiredFields.length,
          ),
      );
      setLoading(false);
    };
    fileReader.onerror = () =>
      setAlertMessages({
        message: "There was an issue uploading the file",
        severity: "error",
        title: "Upload Error",
      });
    fileReader.readAsText(file);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      const program = allPrograms.find(({ name }) => name === selectedProgram);
      const dataFromServer = await saveData({
        client,
        metadataFields: metadataConfiguration[objectType],
        program,
        type: objectType,
        users,
      })(validatedData);
      const savedData = hideUpdatedFields({
        type: objectType,
        uploadedData: validatedData,
      })(dataFromServer);

      const requestDataValidated =
        checkFailedRequestData(savedData)(dataFromServer);

      const hasRequestFailedFields = requestDataValidated?.some(
        (item) => item.requestFailedFields?.length > 0,
      );

      if (hasRequestFailedFields) {
        alertContext.actions.addAlert({
          message: `Save failed`,
          options: { persist: true, variant: "error" },
        });
      } else {
        alertContext.actions.addAlert({
          message: `Save successful`,
          options: {
            variant: "success",
          },
        });
      }
      setValidatedData(requestDataValidated);
    } catch (error) {
      console.error(error, "error");
      alertContext.actions.addAlert({
        message: `Save failed!`,
        options: { persist: true, variant: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const createColumns = (rows) =>
    rows
      .reduce((acc, { data, missingRequiredFields }) => {
        const columns = Object.keys(data).filter((s) => s !== "id");
        return [...new Set([...acc, ...columns, ...missingRequiredFields])];
      }, [])
      .map((key) => ({
        field: key,
        minWidth: 200,
        headerName: key,
        type: "string",
        renderCell: ({ value }) => {
          return key === `Item Name` && !value ? (
            <Check />
          ) : (
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {value}
            </div>
          );
        },
      }));

  const columns = createColumns(validatedData);

  const cellStyles = ({ field, row }) => {
    const {
      action,
      invalidFields = [],
      missingRequiredFields = [],
      updatedFields = [],
      requestFailedFields = [],
    } = row;
    if ([...requestFailedFields].includes(field)) return "failedRequest";
    if ([...invalidFields, ...missingRequiredFields].includes(field))
      return "invalid";
    if (action === "Create" && field === `${_.capitalize(objectType)} Name`)
      return "create";
    if (action === "Update" && updatedFields.includes(field)) return "update";
  };

  const dataGridProps = {
    columns,
    error: null,
    getCellClassName: cellStyles,
    loading,
    rows: validatedData.map(({ data, ...row }, idx) => ({
      id: idx,
      ...row,
      ...data,
    })),
    sx: { overflowX: "scroll" },
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: 2,
          maxWidth: "100%",
          padding: 1,
        }}
      >
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h4">{`${_.capitalize(
            objectType,
          )} Batch Data Upload`}</Typography>
        </Box>
        <form id="batchDataUploadForm" onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              alignItems: "end",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        fullWidth={true}
                        id={`bdu-program`}
                        label="Program"
                        name={`program`}
                        onChange={() => setValidatedData([])}
                        options={userAccessiblePrograms?.map((value) => ({
                          label: value?.name,
                          value: value?.name,
                        }))}
                        rules={{
                          required: true,
                        }}
                      />
                    ),
                    key: "cdu-program",
                  },
                  {
                    children: (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "start",
                            marginBottom: 1,
                          }}
                        >
                          <Button
                            disabled={!selectedProgram}
                            onClick={fileInputOnClick}
                            size="small"
                            startIcon={<ChooseFileIcon />}
                            variant="outlined"
                          >
                            Choose File
                          </Button>
                          <input
                            accept=".csv"
                            id="csv-file-input"
                            onChange={fileInputOnChange}
                            ref={inputRef}
                            style={{ display: "none" }}
                            type="file"
                          />
                          <Typography
                            sx={{ color: "secondary.main", ml: 2, mt: 2 }}
                          >
                            {file && file.name}
                          </Typography>
                          {!validFileType && file && (
                            <Typography
                              sx={{
                                color: "#db485f",
                                mt: 2,
                                fontWeight: "medium",
                              }}
                            >
                              Invalid file type
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                          }}
                        >
                          <Link
                            color="primary"
                            disabled={!selectedProgram}
                            endIcon={<OpenInNewIcon />}
                            name="view-metadata-values"
                            size="small"
                            target="Metadata List"
                            href={metadataListUrl}
                            variant="outlined"
                          >
                            View Metadata Values
                          </Link>
                        </Box>
                      </>
                    ),
                    key: "bdu-csv-file",
                  },
                ],
              ]}
            ></GridGenerator>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Button
                disabled={!selectedProgram || !file || !validFileType}
                onClick={(e) => handleUpload(e)}
                sx={{ marginRight: 2 }}
                variant="contained"
              >
                {`Upload ${_.capitalize(objectType)} Data`}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      {alertMessages?.message && (
        <Box sx={{ marginBottom: 2 }}>
          <Alert severity={alertMessages?.severity}>
            <AlertTitle>{alertMessages?.title}</AlertTitle>
            {alertMessages?.message}
          </Alert>
        </Box>
      )}

      {(!!validatedData.length || loading) && (
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "40em",
            marginTop: 3,
            padding: 2,
            width: "100%",
          }}
        >
          {!loading && (
            <Box sx={{ marginBottom: 3 }}>
              {!isSubmitSuccessful && (
                <Typography
                  sx={{ color: uploadIsValid ? "black" : "#db485f" }}
                  variant="h4"
                >
                  {uploadIsValid
                    ? `Upload is valid`
                    : `Upload rejected due to invalid or missing (required) values`}
                </Typography>
              )}
              <List sx={{ display: "flex" }}>
                <ListItem sx={{ color: "#07b407" }}>Created values</ListItem>
                <ListItem sx={{ color: "blue " }}>Overwritten values</ListItem>
                <ListItem sx={{ color: "#ba394d" }}>
                  Out of range values
                </ListItem>
                <ListItem sx={{ color: "#FBC02D" }}>
                  Failed Request values
                </ListItem>
                <ListItem>Default values</ListItem>
              </List>
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              maxHeight: "25em",
              maxWidth: `calc(${document.body.clientWidth}px - 32px)`,
              "& .create": {
                backgroundColor: "rgba(157, 255, 118, 0.49)",
                color: "#1a3e72",
                fontWeight: "600",
              },
              "& .invalid": {
                backgroundColor: "#ea8897",
                color: "#1a3e72",
                fontWeight: "600",
              },
              "& .update": {
                backgroundColor: "#5294f5",
                fontWeight: "600",
              },
              "& .failedRequest": {
                backgroundColor: "#FBC02D",
                fontWeight: "600",
              },
            }}
          >
            <DataGrid {...dataGridProps}></DataGrid>
          </Box>
          <Box
            sx={{
              alignSelf: "end",
            }}
          >
            <Button
              disabled={!!loading}
              onClick={handleResets}
              sx={{ marginRight: 2, marginTop: 3 }}
              variant="outlined"
            >
              Reset
            </Button>
            <Button
              disabled={!!loading || !uploadIsValid || isSubmitSuccessful}
              form="batchDataUploadForm"
              sx={{ marginRight: 2, marginTop: 3 }}
              type="submit"
              variant="contained"
            >
              Save Changes
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export { BatchDataUpload };
